import { useEffect, useMemo, useState } from 'react';
import { getViolationRuleCatalog } from '../services/violations';
import { isAdmin } from '../services/api';

/**
 * Каталог метрик камеры (GET /admin/violation-rule-catalog) нужен только ради
 * подписей метрик. Эндпоинт доступен администратору, остальным подписи даёт
 * статическая карта в utils/ruleFormat, поэтому для них запрос не делается.
 *
 * Кэш модульный: Map<cameraId, Promise>. Все экраны и строки списка с одной
 * камерой делят один запрос. Неудачный запрос из кэша убирается, чтобы
 * следующий монтаж мог повторить попытку.
 */
const cache = new Map();

export const resetRuleCatalogCacheForTests = () => cache.clear();

const loadCatalog = (cameraId) => {
  if (cache.has(cameraId)) return cache.get(cameraId);

  const promise = Promise.resolve()
    .then(() => getViolationRuleCatalog(cameraId))
    .then((data) => (data && typeof data === 'object' ? data : null))
    .catch(() => {
      if (cache.get(cameraId) === promise) cache.delete(cameraId);
      return null;
    });

  cache.set(cameraId, promise);
  return promise;
};

const normalizeIds = (cameraIds) =>
  [...new Set((cameraIds || []).filter((id) => id !== null && id !== undefined && id !== ''))];

/** Каталоги нескольких камер сразу: { catalogsByCamera: { [cameraId]: catalog } }. */
export const useRuleCatalogs = (cameraIds) => {
  const key = normalizeIds(cameraIds).join('|');
  const ids = useMemo(() => (key ? key.split('|') : []), [key]);
  const [byId, setById] = useState({});

  useEffect(() => {
    if (!ids.length || !isAdmin()) return undefined;
    let cancelled = false;
    Promise.all(ids.map((id) => loadCatalog(id).then((catalog) => [id, catalog]))).then((pairs) => {
      if (cancelled) return;
      const loaded = {};
      pairs.forEach(([id, catalog]) => { if (catalog) loaded[id] = catalog; });
      // Склеиваем с уже загруженными: при смене страницы подписи не мигают.
      setById((prev) => ({ ...prev, ...loaded }));
    });
    return () => { cancelled = true; };
  }, [ids, key]);

  return { catalogsByCamera: byId };
};

export const useRuleCatalog = (cameraId) => {
  const hasCamera = cameraId !== null && cameraId !== undefined && cameraId !== '';
  const [state, setState] = useState({ cameraId: null, catalog: null, done: false });

  useEffect(() => {
    if (!hasCamera || !isAdmin()) return undefined;
    let cancelled = false;
    loadCatalog(cameraId).then((catalog) => {
      if (!cancelled) setState({ cameraId, catalog, done: true });
    });
    return () => { cancelled = true; };
  }, [cameraId, hasCamera]);

  if (!hasCamera || !isAdmin()) return { catalog: null, loading: false };
  const current = state.cameraId === cameraId && state.done;
  return { catalog: current ? state.catalog : null, loading: !current };
};

export default useRuleCatalog;
